import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../api/supabase'
import { useAuth } from '../contexts/AuthContext'
import { formatResolvedAddress, isRecordedPartyAddress, resolveJobPartyAddress, setJobPartyAddress, type JobPartyRole, type ResolvedPartyAddress } from '../lib/jobPartyAddress'

// Sync Preflight, first section: the address each party will be sent with. A sync to BoxTop is permanent, so an address that was never
// recorded on the job (only the company's current default is available) has to be confirmed by a person here -- one click per party, or
// "Confirm all". Reports back whether every party is recorded; the window keeps its Approve button off until it is.
interface Row {
  label: string
  role: JobPartyRole
  customerId: string
  customerName: string
  resolved: ResolvedPartyAddress | null
  error?: string
}

// The consignor / importer / notify parties come from the SERVER's preflight (the same rule the sync uses) -- never recomputed here.
interface ServerParty { role: string; customerId: string; addressRole?: string }
interface Props {
  jobRef: string
  parties: ServerParty[]
  onStatus: (allRecorded: boolean) => void
}

export default function PartyAddressConfirmPanel({ jobRef, parties, onStatus }: Props) {
  const { userEmail } = useAuth()
  const [jobId, setJobId] = useState<string | null>(null)
  const [rows, setRows] = useState<Row[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    const { data: job, error: jErr } = await supabase
      .from('jobs')
      .select('id, job_type, parent_job_id, importer_id, consignor_id, notify_id, hbl_importer_id, hbl_consignor_id, hbl_notify_id, invoice_to_id, overseas_agent_id')
      .eq('job_ref', jobRef).maybeSingle()
    if (jErr || !job) { setError(jErr?.message || 'Job not found'); return }
    setJobId(job.id)
    const fromServer = (role: string): { id: string | null; addrRole: JobPartyRole } => {
      const p = parties.find(x => x.role === role)
      return { id: p?.customerId ?? null, addrRole: (p?.addressRole ?? role) as JobPartyRole }
    }
    const con = fromServer('consignor'), imp = fromServer('importer'), not = fromServer('notify')
    const [impId, impRole, conId, conRole, notId, notRole] = [imp.id, imp.addrRole, con.id, con.addrRole, not.id, not.addrRole]
    const wanted: Array<{ label: string; role: JobPartyRole; id: string | null }> = [
      { label: `Consignor / Shipper${conRole.startsWith('hbl_') ? ' (HBL)' : ''}`, role: conRole, id: conId },
      { label: `Importer / Consignee${impRole.startsWith('hbl_') ? ' (HBL)' : ''}`, role: impRole, id: impId },
      { label: `Notify Party${notRole.startsWith('hbl_') ? ' (HBL)' : ''}`, role: notRole, id: notId },
      { label: 'Customer', role: 'invoice_to' as JobPartyRole, id: job.invoice_to_id },
      { label: 'Overseas Agent', role: 'overseas_agent' as JobPartyRole, id: job.overseas_agent_id },
    ].filter(w => !!w.id)
    const ids = [...new Set(wanted.map(w => w.id as string))]
    const { data: cs } = ids.length ? await supabase.from('customers').select('id, code, name').in('id', ids) : { data: [] }
    const nameOf = new Map((cs ?? []).map(c => [c.id, `${c.code} – ${c.name}`]))
    const out: Row[] = []
    for (const w of wanted) {
      try {
        out.push({ label: w.label, role: w.role, customerId: w.id as string, customerName: nameOf.get(w.id as string) ?? '', resolved: await resolveJobPartyAddress(job.id, w.role) })
      } catch (e) {
        out.push({ label: w.label, role: w.role, customerId: w.id as string, customerName: nameOf.get(w.id as string) ?? '', resolved: null, error: e instanceof Error ? e.message : 'failed' })
      }
    }
    setRows(out)
  }, [jobRef, parties])

  useEffect(() => { load() }, [load])

  const allRecorded = !!rows && rows.every(r => isRecordedPartyAddress(r.resolved))
  useEffect(() => { if (rows) onStatus(allRecorded) }, [rows, allRecorded, onStatus])

  const confirm = async (rs: Row[]) => {
    if (!jobId) return
    setBusy(true); setError(null)
    try {
      for (const r of rs) if (r.resolved?.address_id) await setJobPartyAddress(jobId, r.role, r.resolved.address_id, userEmail)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  if (error && !rows) return <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">Addresses could not be loaded: {error}</div>
  if (!rows) return <p className="text-sm text-gray-500">Checking addresses…</p>

  const confirmable = rows.filter(r => r.resolved?.source === 'provisional' && r.resolved.address_id)
  return (
    <div className={`rounded-lg border p-3 ${allRecorded ? 'border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800' : 'border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700'}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {allRecorded ? 'Addresses to be sent — all recorded on this job' : 'Addresses — confirm before syncing (BoxTop cannot be corrected afterwards)'}
        </h3>
        {confirmable.length > 1 && (
          <button disabled={busy} onClick={() => confirm(confirmable)} className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            Confirm all ({confirmable.length})
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mb-1">{error}</p>}
      <div className="space-y-1.5">
        {rows.map(r => {
          const res = r.resolved
          const ok = isRecordedPartyAddress(res)
          return (
            <div key={`${r.role}-${r.customerId}`} className="flex items-start justify-between gap-3 text-xs">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-800 dark:text-gray-200">{r.label} — <span className="font-normal">{r.customerName}</span></p>
                {res && formatResolvedAddress(res) ? <p className="text-gray-600 dark:text-gray-300">{formatResolvedAddress(res)}</p> : null}
                {r.error && <p className="text-red-600">{r.error}</p>}
                {res && !ok && <p className={res.source === 'provisional' ? 'text-amber-700 dark:text-amber-400' : 'text-red-600'}>{res.reason}</p>}
              </div>
              {ok ? (
                <span className="shrink-0 px-2 py-0.5 rounded bg-green-100 text-green-700 font-medium">{res!.source === 'chosen' ? 'Chosen' : 'Recorded'}</span>
              ) : res?.source === 'provisional' && res.address_id ? (
                <button disabled={busy} onClick={() => confirm([r])} className="shrink-0 px-2 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50">Use this address</button>
              ) : (
                <Link to={`/customers/${r.customerId}`} target="_blank" className="shrink-0 px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50">Fix on company page</Link>
              )}
            </div>
          )
        })}
      </div>
      {!allRecorded && <p className="mt-2 text-[11px] text-gray-500">To use a different address, close this window and use "Change address" on the job's Addresses tab.</p>}
    </div>
  )
}
